import React from 'react';
import { Chip, Tooltip, Snippet } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  UserCheckIc,
  GroupUserIc,
  LinkIcon,
  ChartSquare,
  ArrowGreen,
} from '../../../../components/icons';
import { OrderData } from '../types';
import OrderStatsTooltip from './OrderStatsTooltip';

interface CampaignCellProps {
  order: OrderData;
}

const CampaignCell: React.FC<CampaignCellProps> = ({ order }) => {
  const { t } = useTranslations();

  return (
    <div className="flex flex-col gap-1">
      <ResponsiveTooltip
        content={<OrderStatsTooltip statistics={order.campaign.statistics} />}
        className="p-4"
      >
        <div className="flex items-center gap-2 cursor-pointer">
          <UserCheckIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {order.campaign.statistics.verifiedUsers}/{order.campaign.statistics.totalUsers}
          </Typography>
          <GroupUserIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {order.campaign.statistics.verifiedUsers}
          </Typography>
          <ChartSquare />
        </div>
      </ResponsiveTooltip>
      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('orders.campaign.transactions')}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          ${order.campaign.statistics.transactions.total.toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('orders.campaign.cost')}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          $
          {(typeof order.campaign.statistics.cost === 'number'
            ? order.campaign.statistics.cost
            : parseFloat(order.campaign.statistics.cost) || 0
          ).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('orders.campaign.arpu')}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          $
          {(typeof order.campaign.statistics.arpu === 'number'
            ? order.campaign.statistics.arpu
            : parseFloat(order.campaign.statistics.arpu) || 0
          ).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('orders.campaign.arpuUsd')}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          $
          {(typeof order.campaign.statistics.arpuUsd === 'number'
            ? order.campaign.statistics.arpuUsd
            : parseFloat(order.campaign.statistics.arpuUsd) || 0
          ).toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('orders.campaign.romi')}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          {(typeof order.campaign.statistics.romi === 'number'
            ? order.campaign.statistics.romi
            : parseFloat(order.campaign.statistics.romi) || 0
          ).toFixed(2)}
          %
        </Typography>
      </div>

      <Chip
        className="bg-green-100 text-green-800 rounded-lg w-fit"
        size="sm"
        startContent={<ArrowGreen />}
      >
        <Typography variant="body3" className="text-green-800">
          {order.campaign.transitions_count} ({order.campaign.transitions_percentage})
        </Typography>
      </Chip>

      <Snippet
        symbol={null}
        codeString={order.campaign.link}
        className="bg-husky rounded-full px-2 py-[0.1875rem]"
        classNames={{
          base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
          pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
          copyButton: 'text-partner min-w-5 h-5',
          checkIcon: 'text-partner',
          content: 'flex items-center gap-1 max-w-[13.75rem]',
        }}
      >
        <a
          href={order.campaign.link}
          target="__blank"
          className="flex items-center gap-1 no-underline"
        >
          <LinkIcon size={18} fill="#006FEE" className="text-partner" />
          <Typography variant="body2" className="!text-partner truncate max-w-[11.25rem]">
            {order.campaign.link}
          </Typography>
        </a>
      </Snippet>
    </div>
  );
};

export default CampaignCell;
